import React from 'react';
import {
  Wrench,
  ShieldCheck,
  CreditCard,
  MapPin,
  CheckCircle2,
  Bell,
  Send,
  Camera,
  FileCheck,
  Compass,
  ArrowRight,
  UserCheck,
  HelpCircle,
  Briefcase,
  Lock,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { CategoryIcon } from '../../components/common/CategoryIcon';

export const BecomeProfessionalPage: React.FC = () => {
  const { navigateTo, currentUser } = useApp();

  const verificationPillars = [
    {
      title: '1. Government Identity (NIN)',
      desc: 'Verify with your National Identity Number (NIN Slip/Card), International Passport, Driver’s License, or Voter’s Card to validate legal identity.',
      icon: FileCheck,
    },
    {
      title: '2. Address & Utility Proof',
      desc: 'Submit a recent utility bill (PHCN electricity, water, waste, or tenancy document) confirming your Nigerian operational base.',
      icon: MapPin,
    },
    {
      title: '3. Service Area & Location',
      desc: 'Register the specific Nigerian State and Local Government Areas (LGAs) where you actively provide services without long travel delays.',
      icon: Compass,
    },
    {
      title: '4. Real-time Facial Liveness',
      desc: 'Perform a live camera face scan to ensure you match your government-issued identity document and protect against impersonation.',
      icon: Camera,
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8 space-y-12">
      {/* Hero Section */}
      <div className="max-w-5xl mx-auto bg-slate-900 text-white rounded-3xl p-6 sm:p-12 border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#10b981_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none" />
        <div className="relative max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold">
            <Wrench className="w-4 h-4 text-emerald-400" />
            <span>Join Nigeria’s Verified Professional Network</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
            Grow Your Technical Trade Business with JobMatch NG
          </h1>

          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            Connect with real homeowners, facility managers, and businesses looking for skilled Nigerian
            craftspeople. Get verified, receive matching job alerts in your LGA, and submit competitive quotes.
          </p>

          {currentUser?.role === 'professional' ? (
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                onClick={() => navigateTo('pro_dashboard')}
                className="px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-emerald-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Go to Professional Dashboard</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                onClick={() => navigateTo('pro_auth', { authMode: 'register' })}
                className="px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-emerald-600/30 transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Register as a Professional</span>
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigateTo('pro_auth', { authMode: 'login' })}
                className="px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs sm:text-sm font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Sign In to Pro Account</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Who Can Register */}
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Who Can Register on JobMatch NG?
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl mx-auto">
            We welcome skilled and accountable Nigerian tradespeople, technicians, and service enterprises.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-lg">
              🛠️
            </div>
            <h3 className="font-bold text-sm text-slate-900">Independent Professionals</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Experienced plumbers, electricians, carpenters, painters, and tilers with proven field skills.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-2">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center font-bold text-lg">
              ⚡
            </div>
            <h3 className="font-bold text-sm text-slate-900">Technical Specialists</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              AC refrigeration engineers, diesel/petrol generator mechanics, and solar inverter technicians.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-2">
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold text-lg">
              🪑
            </div>
            <h3 className="font-bold text-sm text-slate-900">Makers & Fabricators</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Custom furniture makers, aluminum fabricators, security gate welders, and bricklayers.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-xs space-y-2">
            <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center font-bold text-lg">
              🏢
            </div>
            <h3 className="font-bold text-sm text-slate-900">Workshops & Firms</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Registered technical businesses and maintenance agencies seeking commercial and residential contracts.
            </p>
          </div>
        </div>
      </div>

      {/* Supported Categories */}
      <div className="max-w-5xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Supported Service Trades
            </h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Select one or more trades during registration to receive matching client leads.
            </p>
          </div>
          <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200 w-fit">
            {SERVICE_CATEGORIES.length} Active Categories
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {SERVICE_CATEGORIES.map((cat) => (
            <div
              key={cat.slug}
              className="p-3.5 rounded-2xl border border-slate-200 bg-slate-50/70 hover:bg-emerald-50/50 hover:border-emerald-200 transition-colors space-y-1.5"
            >
              <div className="flex items-center gap-2">
                <CategoryIcon slug={cat.slug} className="w-4 h-4 text-emerald-600 shrink-0" />
                <h4 className="font-bold text-xs text-slate-900 truncate">{cat.name}</h4>
              </div>
              <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">
                {cat.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* 4-Step Verification Requirement */}
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
            <span>Integrity & Verification</span>
          </span>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
            Mandatory Verification Requirements
          </h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-xl mx-auto">
            To protect both customers and genuine service providers, all professionals must complete our 4-step
            sequential verification before quotes can be submitted.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {verificationPillars.map((v) => {
            const Icon = v.icon;
            return (
              <div
                key={v.title}
                className="bg-white rounded-3xl p-6 border border-slate-200 shadow-xs flex items-start gap-4"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-bold text-base text-slate-900">{v.title}</h3>
                  <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{v.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-slate-100 rounded-2xl p-4 text-xs text-slate-600 flex items-start gap-2 max-w-3xl mx-auto">
          <Lock className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
          <span>
            <strong>Privacy Guarantee:</strong> Identity documents and facial scans are encrypted and used solely
            to confirm professional eligibility under NDPA 2023 guidelines.
          </span>
        </div>
      </div>

      {/* Marketplace Commission Model Section */}
      <div className="max-w-4xl mx-auto bg-white rounded-3xl p-6 sm:p-10 border border-slate-200 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-5">
          <div>
            <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
              Artisan Marketplace Model
            </span>
            <h2 className="text-2xl font-extrabold text-slate-900 mt-0.5">
              Free Access & Transparent 10% Commission
            </h2>
          </div>
          <div className="text-left sm:text-right">
            <span className="text-3xl font-extrabold text-slate-900">₦0</span>
            <span className="text-xs text-slate-500 block">No Monthly Subscription</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs sm:text-sm text-slate-700">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>Instant push & in-app alerts for all matching jobs in your registered LGAs.</span>
          </div>
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>Submit unlimited itemized bids with detailed labor and material costings.</span>
          </div>
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>Guaranteed Paystack Escrow deposits before you commence work on-site.</span>
          </div>
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <span>You take home 90% net earnings paid directly into your Nigerian bank account.</span>
          </div>
        </div>

        <div className="pt-2 text-xs text-slate-500 border-t border-slate-100">
          Zero upfront or monthly charges. JobMatch NG earns only a 10% platform commission when a client successfully pays for your completed trade work.
        </div>
      </div>

      {/* Final Action CTA */}
      <div className="max-w-4xl mx-auto bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-xl">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
          Ready to Start Receiving Real Jobs in Nigeria?
        </h2>
        <p className="text-xs sm:text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
          Join hundreds of vetted professionals delivering quality trade services across Lagos, Abuja, Port
          Harcourt, and across the nation.
        </p>

        <button
          id="become-pro-final-cta-btn"
          onClick={() => navigateTo('pro_auth', { authMode: 'register' })}
          className="px-8 py-4 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-600/30 transition-all inline-flex items-center gap-2 cursor-pointer"
        >
          <span>Register as a Professional</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
